import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';

import * as goalsActions from 'Shared/actions/Goals.actions';
import { useToggle } from 'Shared/utils/hooks';
import WpIcon from 'Wpds/components/icon/WpIcon';
import Colors from 'Wpds/colors/ColorConstants';
import { GoalStatusesLabel, GoalItem } from 'Shared/constants/types';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

import './GoalStatusChangeDropdown.scss';

const statuses = ['on_track', 'at_risk', 'missed', 'no_status', 'achieved'];
const classNamePrefix = 'GoalStatusChangeDropdown';

const StatusLabel = status => {
  const classes = classNames(
    `${classNamePrefix}__status-label__indicator`,
    `${classNamePrefix}__status-label__indicator--${status}`
  );
  return (
    <span className={`${classNamePrefix}__status-label`}>
      <span className={classes} />
      {GoalStatusesLabel[status]}
    </span>
  );
};

const DropdownItems = (handleSetStatus, currentStatus) => {
  return (
    <>
      {statuses
        .filter(status => status !== currentStatus)
        .map(status => (
          <div key={status} className="GoalStatusChangeDropdown__item" onClick={() => handleSetStatus(status)}>
            {StatusLabel(status)}
          </div>
        ))}
    </>
  );
};

type GoalStatusChangeDropdownProps = {
  goal: GoalItem;
};

const GoalStatusChangeDropdown = ({ goal }: GoalStatusChangeDropdownProps) => {
  const dispatch = useDispatch();

  const [isDropdownOpen, toggleDropdownOpen] = useToggle(false);

  const status = goal.status;
  const isComplete = goal.complete;
  const { id: userId } = useCurrentUser();
  const canChangeStatus = (userId === goal.assignerId || userId === goal.assigneeId) && !isComplete;

  const handleSetStatus = useCallback(
    newStatus => {
      dispatch(goalsActions.changeStatus(goal.slug, newStatus));
      toggleDropdownOpen();
    },
    [toggleDropdownOpen, dispatch, goal]
  );

  return (
    <div className={classNamePrefix}>
      {canChangeStatus ? (
        <Dropdown isOpen={isDropdownOpen} toggle={toggleDropdownOpen}>
          <DropdownToggle
            className={`${classNamePrefix}__toggle`}
            tag="span"
            data-toggle="dropdown"
            aria-expanded={isDropdownOpen}
          >
            <span>{StatusLabel(status)}</span>
            <WpIcon name="caret-down" color={Colors.gray[500]} />
          </DropdownToggle>
          <DropdownMenu className="GoalStatusChangeDropdown__dropdown">
            {DropdownItems(handleSetStatus, status)}
          </DropdownMenu>
        </Dropdown>
      ) : (
        <h5 className="mb-0">{StatusLabel(status)}</h5>
      )}
    </div>
  );
};

export default GoalStatusChangeDropdown;
